// Pure Shopify helpers the Connections page renders from. No DOM here: the
// web test suite runs without one, so anything worth asserting lives in this
// module and the component stays a thin shell.

// The closed set of reason codes the OAuth callback redirects with, mapped to
// sentences a person can act on. A reason outside the set falls back to the
// generic sentence rather than leaking the raw code.
export const SHOPIFY_CONNECT_ERRORS: Record<string, string> = {
  declined: "The store authorization was cancelled, so nothing was connected.",
  state_missing: "That connect link was not one we started. Try connecting again.",
  state_expired: "The store authorization took too long. Try connecting again.",
  hmac_invalid: "Shopify's signature on the redirect did not verify. Try connecting again.",
  shop_mismatch: "The store that answered is not the one this connection was started for. Try again.",
  token_exchange_failed: "Shopify refused to complete the connection. Try again.",
  not_configured: "Shopify is not configured on this server yet — ask an administrator.",
  not_allowed: "Your access to connections changed while the store was being authorized, so nothing was connected.",
}

export type ShopifyOutcome = { kind: "connected" | "error"; message: string }

/** The ?shopify=…&reason=… the callback redirects with, or null when this is
 *  not a Shopify return. */
export function shopifyOutcome(params: URLSearchParams): ShopifyOutcome | null {
  const outcome = params.get("shopify")
  if (!outcome) return null
  if (outcome === "connected") {
    return { kind: "connected", message: "Shopify store connected. Your agent sessions can now work with it." }
  }
  return {
    kind: "error",
    message: SHOPIFY_CONNECT_ERRORS[params.get("reason") ?? ""] ?? "Connecting to Shopify failed.",
  }
}

// Same shape the server enforces, so the Connect button is only enabled for a
// value the server will accept. The server re-validates; this is courtesy.
const SHOP_DOMAIN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/

/** Lowercase, strip a scheme and any path, then check the shape. Null when the
 *  input is not a *.myshopify.com store. */
export function normaliseShopDomain(input: string): string | null {
  const value = input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
  return SHOP_DOMAIN.test(value) ? value : null
}
